import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import CONFIG from '../config';
import logo from '../assets/logo.png';

const Login = ({ onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetToken, setResetToken] = useState('');

    const [view, setView] = useState('login'); // login, forgot, verify-otp, reset-password
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        localStorage.clear();
        setError('');
        setLoading(true);
        try {
            const resp = await axios.post(`${CONFIG.API_URL}/api/auth/login`, { email, password });
            const { user, token } = resp.data;

            const rolesWithDashboards = ['ADMIN', 'RUN_OFFICER', 'STAGE_EVALUATOR', 'DOCTOR', 'FIELD_MONITOR'];
            if (rolesWithDashboards.includes(user.role)) {
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));
                onLoginSuccess(user);
            } else {
                Swal.fire({
                    title: 'Dashboard Under Development',
                    text: 'Your specialized dashboard is currently in the development process. Please check back later.',
                    icon: 'info',
                    confirmButtonText: 'Understood',
                    confirmButtonColor: '#a52a2a'
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            await axios.post(`${CONFIG.API_URL}/api/auth/send-otp`, { email });
            setMessage('Verification code sent to your email');
            setView('verify-otp');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const resp = await axios.post(`${CONFIG.API_URL}/api/auth/verify-otp`, { email, otp });
            setResetToken(resp.data.resetToken);
            setMessage('OTP Verified. Please set a new password.');
            setView('reset-password');
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid or expired OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await axios.post(`${CONFIG.API_URL}/api/auth/reset-password`, { resetToken, newPassword });
            setMessage('Password updated successfully. Please login.');
            setView('login');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-wrapper">
            <div className="auth-container">
                <img src={logo} alt="ICFF India Logo" className="auth-logo" />
                <h1 className="auth-title">ICFF INDIA</h1>
                <p className="auth-subtitle">Official Recruitment & Personnel Management Portal</p>

                {error && <div style={{ color: '#e53e3e', background: '#fff5f5', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}
                {message && <div style={{ color: '#38a169', background: '#f0fff4', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>{message}</div>}

                {view === 'login' && (
                    <form onSubmit={handleLogin}>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                className="input-field"
                                placeholder="admin@icff.in"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Password</label>
                            <input
                                type="password"
                                className="input-field"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Authenticating...' : 'Sign In'}
                        </button>
                        <div className="auth-footer">
                            <button type="button" className="footer-link" onClick={() => setView('forgot')}>
                                Forgot Password?
                            </button>
                        </div>
                    </form>
                )}

                {view === 'forgot' && (
                    <form onSubmit={handleSendOTP}>
                        <div className="form-group">
                            <label>Registered Email</label>
                            <input
                                type="email"
                                className="input-field"
                                placeholder="Enter your registered email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Sending Code...' : 'Send Verification Code'}
                        </button>
                        <div className="auth-footer">
                            <button type="button" className="footer-link" onClick={() => setView('login')}>
                                Back to Login
                            </button>
                        </div>
                    </form>
                )}

                {view === 'verify-otp' && (
                    <form onSubmit={handleVerifyOTP}>
                        <div className="form-group">
                            <label>Verification Code</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="6-digit code"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength="6"
                                required
                            />
                        </div>
                        <button type="submit" className="btn-primary" disabled={loading}>Verify & Continue</button>
                        <div className="auth-footer">
                            <button type="button" className="footer-link" onClick={() => setView('forgot')}>
                                Resend Code
                            </button>
                        </div>
                    </form>
                )}

                {view === 'reset-password' && (
                    <form onSubmit={handleResetPassword}>
                        <div className="form-group">
                            <label>New Password</label>
                            <input
                                type="password"
                                className="input-field"
                                placeholder="Enter at least 8 characters"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                minLength="8"
                                required
                            />
                        </div>
                        <button type="submit" className="btn-primary" disabled={loading}>Update Password</button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
